import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Alert
} from '@mui/material';
import {
  Pets,
  CheckCircle,
  LocalHospital,
  Flight,
  DirectionsCar,
  Home,
  Info,
  FavoriteBorder,
  Description
} from '@mui/icons-material';

function MovingWithPets() {
  const petTypes = [
    {
      pet: 'Hunde',
      icon: '🐕',
      tips: [
        'Transportbox oder Sicherheitsgurt im Auto verwenden',
        'Lieblingsspielzeug und Decke mitnehmen',
        'Regelmäßige Pausen bei längeren Fahrten',
        'Wasser und Napf griffbereit halten',
        'Beruhigungsmittel nur nach Rücksprache mit Tierarzt'
      ]
    },
    {
      pet: 'Katzen',
      icon: '🐱',
      tips: [
        'Stabile Transportbox verwenden',
        'Box mit vertrauter Decke auslegen',
        'Katze am Umzugstag in ruhigem Raum unterbringen',
        'Erst nach kompletter Einrichtung freilassen',
        'Gewohnte Katzentoilette sofort aufstellen'
      ]
    },
    {
      pet: 'Kleintiere',
      icon: '🐹',
      tips: [
        'Im gewohnten Käfig transportieren',
        'Käfig gegen Verrutschen sichern',
        'Vor direkter Sonneneinstrahlung schützen',
        'Genügend Futter und Wasser bereitstellen',
        'Stressvermeidung durch ruhige Umgebung'
      ]
    },
    {
      pet: 'Vögel',
      icon: '🦜',
      tips: [
        'Käfig mit Tuch abdecken',
        'Sitzstangen während Transport entfernen',
        'Wassernäpfe leeren (feuchtes Futter anbieten)',
        'Zugluft vermeiden',
        'Ruhig und gleichmäßig transportieren'
      ]
    }
  ];

  const preparation = [
    {
      title: 'Tierarztbesuch',
      icon: <LocalHospital sx={{ color: '#55d6dc' }} />,
      tasks: [
        'Gesundheitscheck durchführen lassen',
        'Impfungen auffrischen',
        'EU-Heimtierausweis besorgen (bei Auslandsumzug)',
        'Beruhigungsmittel besprechen',
        'Chipnummer überprüfen'
      ]
    },
    {
      title: 'Dokumente',
      icon: <Description sx={{ color: '#55d6dc' }} />,
      tasks: [
        'Impfpass aktualisieren',
        'Versicherungsunterlagen kopieren',
        'Tierarzt-Kontakte notieren',
        'Registrierung bei Tasso/Findefix',
        'Mietvertrag auf Tierhaltung prüfen'
      ]
    },
    {
      title: 'Neue Umgebung',
      icon: <Home sx={{ color: '#55d6dc' }} />,
      tasks: [
        'Neuen Tierarzt in der Nähe suchen',
        'Tierfachgeschäfte erkunden',
        'Gassi-Routen auskundschaften',
        'Nachbarn über Haustiere informieren',
        'Notfall-Tierklinik lokalisieren'
      ]
    }
  ];

  const transportOptions = [
    {
      method: 'Auto',
      icon: <DirectionsCar sx={{ fontSize: 40, color: '#ffd700' }} />,
      pros: [
        'Flexibilität bei Pausen',
        'Vertraute Umgebung',
        'Direkter Transport',
        'Keine fremden Gerüche'
      ]
    },
    {
      method: 'Flugzeug',
      icon: <Flight sx={{ fontSize: 40, color: '#ffd700' }} />,
      pros: [
        'Schneller Transport',
        'Professionelle Betreuung',
        'Klimatisierte Räume',
        'International möglich'
      ]
    }
  ];

  const checklistItems = [
    'Transportbox rechtzeitig besorgen und Tier daran gewöhnen',
    'Futter- und Wasservorrat für mehrere Tage',
    'Medikamente und Erste-Hilfe-Set',
    'Spielzeug und Kuscheldecke',
    'Halsband mit aktueller Adresse',
    'Aktuelle Fotos vom Tier',
    'Kotbeutel/Katzenstreu',
    'Reinigungsmittel für Notfälle'
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1f28 0%, #162a34 50%, #1c3440 100%)'
    }}>
      <Container maxWidth="lg" sx={{ pt: 12, pb: 6 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Pets sx={{ fontSize: 80, color: '#55d6dc', mb: 3 }} />
          <Typography variant="h2" sx={{ color: 'white', fontWeight: 'bold', mb: 3 }}>
            Umzug mit Haustieren
          </Typography>
          <Typography variant="h5" sx={{ color: '#55d6dc', mb: 2 }}>
            Stressfrei umziehen mit Ihren vierbeinigen Freunden
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, maxWidth: 800, mx: 'auto' }}>
            Ein Umzug ist für Haustiere oft stressig. Mit der richtigen Vorbereitung und unseren 
            Tipps machen Sie den Umzug für Ihre Lieblinge so angenehm wie möglich.
          </Typography>
        </Box>

        {/* Important Alert */}
        <Alert 
          severity="info" 
          icon={<Info />}
          sx={{ 
            mb: 6,
            bgcolor: 'rgba(85,214,220,0.1)',
            color: 'white',
            '& .MuiAlert-icon': { color: '#55d6dc' }
          }}
        >
          <Typography variant="body1">
            <strong>Tipp:</strong> Beginnen Sie mindestens 4 Wochen vor dem Umzug mit der Vorbereitung 
            Ihrer Haustiere. Gewöhnen Sie sie langsam an Transportboxen und neue Situationen.
          </Typography>
        </Alert>

        {/* Pet Types */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Tipps nach Tierart
          </Typography>
          <Grid container spacing={4}>
            {petTypes.map((type, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card sx={{
                  bgcolor: '#1a3a44',
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.08)',
                  height: '100%'
                }}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                      <Box sx={{ fontSize: '2.5rem' }}>{type.icon}</Box>
                      <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                        {type.pet}
                      </Typography>
                    </Stack>
                    <List>
                      {type.tips.map((tip, idx) => (
                        <ListItem key={idx} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <FavoriteBorder sx={{ color: '#55d6dc', fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={tip}
                            sx={{ color: 'white', opacity: 0.9 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Preparation Steps */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Wichtige Vorbereitungen
          </Typography>
          <Grid container spacing={4}>
            {preparation.map((prep, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper
                  sx={{
                    p: 3,
                    bgcolor: '#275664',
                    borderRadius: 2,
                    height: '100%',
                    border: '1px solid rgba(255,255,255,0.08)'
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    {prep.icon}
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                      {prep.title}
                    </Typography>
                  </Stack>
                  <List>
                    {prep.tasks.map((task, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CheckCircle sx={{ color: '#55d6dc', fontSize: 16 }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={task}
                          sx={{ 
                            color: 'white', 
                            opacity: 0.85,
                            '& .MuiListItemText-primary': { fontSize: '0.9rem' }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Transport Options */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
            Transportmöglichkeiten
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {transportOptions.map((option, index) => (
              <Grid item xs={12} sm={6} md={5} key={index}>
                <Card sx={{
                  bgcolor: '#1a3a44',
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.08)'
                }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Box sx={{ mb: 2 }}>{option.icon}</Box>
                    <Typography variant="h5" sx={{ color: 'white', mb: 3, fontWeight: 'bold' }}>
                      {option.method}
                    </Typography>
                    <List>
                      {option.pros.map((pro, idx) => (
                        <ListItem key={idx}>
                          <ListItemIcon>
                            <CheckCircle sx={{ color: '#55d6dc' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={pro}
                            sx={{ color: 'white', opacity: 0.9 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Checklist */}
        <Card sx={{ 
          bgcolor: 'rgba(255,255,255,0.05)', 
          backdropFilter: 'blur(10px)',
          mb: 6
        }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 'bold' }}>
              Checkliste für den Umzugstag
            </Typography>
            <Grid container spacing={2}>
              {checklistItems.map((item, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle sx={{ color: '#55d6dc' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={item}
                      sx={{ color: 'white' }}
                    />
                  </ListItem>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <Box sx={{ 
          bgcolor: '#275664', 
          borderRadius: 3,
          p: 4,
          textAlign: 'center'
        }}>
          <Typography variant="h4" sx={{ color: 'white', mb: 2, fontWeight: 'bold' }}>
            Wir kümmern uns um alles – auch um Ihre Haustiere
          </Typography>
          <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, mb: 3 }}>
            Unsere erfahrenen Umzugshelfer wissen, wie man mit Haustieren umgeht und 
            sorgen für einen stressfreien Umzug für die ganze Familie.
          </Typography>
          <Box
            component="button"
            onClick={() => window.location.href = '/contact'}
            style={{
              backgroundColor: '#55d6dc',
              color: '#275664',
              padding: '12px 32px',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer'
            }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#4bc2c8')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#55d6dc')}
          >
            Beratung anfordern
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default MovingWithPets;